import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as Fa6Icons from 'react-icons/fa6';
import { generateSEOFriendlyURL } from '../utils/shareUtils';

const ShareButton = ({
  language, calculatedDays, detailedTime, relationshipLabel,
  anniversaryDate, anniversaryType, petType, petName, shareUrl
}) => {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const shareMenuRef = useRef(null);

  const formatDate = (date) => {
    if (!date) return '';
    if (language === 'zh') {
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    }
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getShareText = () => {
    const petNameText = petName ? petName : (language === 'zh' ? '我的寵物' : 'my pet');
    const dateText = formatDate(anniversaryDate);
    if (language === 'zh') {
      return `${petNameText}${relationshipLabel}已經 ${calculatedDays} 天啦！從 ${dateText} 開始，我們已經一起度過了 ${detailedTime?.years || 0} 年 ${detailedTime?.months || 0} 個月 ${detailedTime?.days || 0} 天。來看看我們的紀念日吧！`;
    }
    return `${petNameText}'s ${relationshipLabel} has been ${calculatedDays} days! Since ${dateText}, we've spent ${detailedTime?.years || 0} years, ${detailedTime?.months || 0} months, and ${detailedTime?.days || 0} days together. Check out our anniversary!`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const shareViaFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(getShareText())}`;
    window.open(url, '_blank');
  };

  const shareViaTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareText())}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank');
  };

  const shareViaThreads = () => {
    const url = `https://www.threads.com/intent/post?text=${encodeURIComponent(`${getShareText()} ${shareUrl}`)}`;
    window.open(url, '_blank');
  };

  const shareViaInstagram = () => {
    copyToClipboard();
    window.open('https://www.instagram.com/homy.hk', '_blank');
  };

  const shareViaWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${getShareText()} ${shareUrl}`)}`;
    window.open(url, '_blank');
  };

  const shareViaLine = () => {
    const url = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(getShareText())}`;
    window.open(url, '_blank');
  };

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={shareMenuRef}>
      <motion.button
        onClick={() => setShowShareMenu(!showShareMenu)}
        className="p-2 bg-love-100 dark:bg-love-900/30 hover:bg-love-200 dark:hover:bg-love-800/50 text-love-600 dark:text-love-400 rounded-full transition-colors"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label={language === 'zh' ? '分享' : 'Share'}
        disabled={!anniversaryDate}
      >
        <SafeIcon icon={FiIcons.FiShare2} className="text-sm" />
      </motion.button>

      <AnimatePresence>
        {showShareMenu && (
          <motion.div
            className="absolute right-0 mt-2 w-52 bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden z-50"
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="py-2">
              <div className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                {language === 'zh' ? '分享紀念日' : 'Share Anniversary'}
              </div>

              <button onClick={copyToClipboard} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center transition-colors">
                <SafeIcon icon={FiIcons.FiLink} className="mr-3 text-gray-500 dark:text-gray-400" />
                {copySuccess ? (language === 'zh' ? '已複製！' : 'Copied!') : (language === 'zh' ? '複製連結' : 'Copy Link')}
              </button>

              <button onClick={shareViaFacebook} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center transition-colors">
                <SafeIcon icon={FiIcons.FiFacebook} className="mr-3 text-blue-600" />
                Facebook
              </button>

              <button onClick={shareViaThreads} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center transition-colors">
                <SafeIcon icon={Fa6Icons.FaThreads} className="mr-3 text-gray-800 dark:text-gray-200" />
                Threads
              </button>

              <button onClick={shareViaInstagram} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center transition-colors">
                <SafeIcon icon={FiIcons.FiInstagram} className="mr-3 text-pink-500" />
                Instagram {language === 'zh' ? '（複製文字）' : '(Copy text)'}
              </button>

              <button onClick={shareViaTwitter} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center transition-colors">
                <SafeIcon icon={FiIcons.FiTwitter} className="mr-3 text-blue-400" />
                Twitter / X
              </button>

              <button onClick={shareViaWhatsApp} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center transition-colors">
                <span className="mr-3 text-green-500 font-bold text-sm">W</span>
                WhatsApp
              </button>

              <button onClick={shareViaLine} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center transition-colors">
                <span className="mr-3 text-green-600 font-bold text-sm">L</span>
                LINE
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShareButton;